import React, {useState} from 'react';
import moment from 'moment';
import {useIntl} from 'react-intl';
import {Modal, DateRangePicker, ColoredIdIcon, FlexColumn, FlexRow, Label, Text, Alert} from '@forecast-it/design-system';
import {showModal, MODAL_TYPE} from '../generic_modal_conductor';
import {createToast} from '../../toasts/toast';
import Util from '../../../util/util';
import UpdateProjectMutation from '../../../../../mutations/update_project_mutation.modern';

export type AddOpportunityDatesModalPayload = {
	projectId: string;
	// Read-only context rows: the coloured "P<id>" badge + project name, and the client.
	projectName?: string | null;
	projectCustomId?: string | null;
	projectColor?: string | null;
	clientName?: string | null;
	// Whichever dates the opportunity already has; they pre-fill the range so saving never drops them.
	projectStartYear?: number | null;
	projectStartMonth?: number | null;
	projectStartDay?: number | null;
	projectEndYear?: number | null;
	projectEndMonth?: number | null;
	projectEndDay?: number | null;
	// Refetch the list so the row's date cells fill in.
	onSuccess?: () => void;
};

export const showAddOpportunityDatesModal = (payload: AddOpportunityDatesModalPayload) =>
	showModal({type: MODAL_TYPE.ADD_OPPORTUNITY_DATES, ...payload});

// A project stores its dates as separate y/m/d numbers; the DateRangePicker works in ISO strings.
export const partsToIso = (year?: number | null, month?: number | null, day?: number | null): string | undefined =>
	year && month && day ? moment({year, month: month - 1, day}).format('YYYY-MM-DD') : undefined;

// The DateRangePicker's onChange receives one interval object of ISO strings, not two positional Dates.
export type DateInterval = {startDate?: string; endDate?: string};

// Maps the picked range onto UpdateProjectInput. Both halves are always sent — cleared ends go as explicit
// nulls so the backend unsets them (mirroring changeStartDate/changeEndDate in ProjectSettingsMutations).
// Pure + exported so the mapping is unit-tested without mounting the modal.
export const buildAddDatesInput = (
	projectId: string,
	interval: DateInterval
	// eslint-disable-next-line @typescript-eslint/no-explicit-any -- UpdateProjectInput isn't typed on the JS mutation module
): any => {
	const start = interval.startDate ? moment(interval.startDate) : null;
	const end = interval.endDate ? moment(interval.endDate) : null;
	return {
		project: {id: projectId},
		projectStartYear: start ? start.year() : null,
		projectStartMonth: start ? start.month() + 1 : null,
		projectStartDay: start ? start.date() : null,
		projectEndYear: end ? end.year() : null,
		projectEndMonth: end ? end.month() + 1 : null,
		projectEndDay: end ? end.date() : null,
	};
};

type Props = AddOpportunityDatesModalPayload & {closeModal: () => void};

// A label + value stack, matching the design's read-only context rows (medium-weight label above the value).
const ContextRow = ({label, children}: {label: string; children: React.ReactNode}) => (
	<FlexColumn gap="xxs">
		<Label>{label}</Label>
		{children}
	</FlexColumn>
);

export const AddOpportunityDatesModal = ({
	closeModal,
	onSuccess,
	projectId,
	projectName,
	projectCustomId,
	projectColor,
	clientName,
	projectStartYear,
	projectStartMonth,
	projectStartDay,
	projectEndYear,
	projectEndMonth,
	projectEndDay,
}: Props): JSX.Element => {
	const {formatMessage} = useIntl();

	// One range covers both dates, so the same modal serves the start-missing and end-missing affordances.
	// Seeding it with the dates the project already has means saving after filling in only the missing half
	// re-sends the other half unchanged instead of clearing it.
	const initial: DateInterval = {
		startDate: partsToIso(projectStartYear, projectStartMonth, projectStartDay),
		endDate: partsToIso(projectEndYear, projectEndMonth, projectEndDay),
	};
	const [interval, setInterval] = useState<DateInterval>(initial);
	// Guards against a rapid double-click firing two update mutations.
	const [saving, setSaving] = useState(false);

	// Nothing to commit until the range actually differs from what the row already had.
	const unchanged = interval.startDate === initial.startDate && interval.endDate === initial.endDate;

	const handleAdd = () => {
		if (unchanged || saving) return;
		setSaving(true);
		Util.CommitMutation(UpdateProjectMutation, buildAddDatesInput(projectId, interval), () => {
			// Same full-width top success banner as the win-probability update (DS SUCCESS Alert via the
			// toaster's "banner" variant).
			createToast({
				duration: 5000,
				variant: 'banner',
				message: (
					<Alert
						type={Alert.TYPE.SUCCESS}
						text={formatMessage({id: 'opportunities.add_project_dates.toast'}, {project: projectName ?? ''})}
					/>
				),
			});
			closeModal();
			onSuccess?.();
		});
	};

	return (
		<Modal onCloseModal={closeModal} size={Modal.SIZE.MEDIUM}>
			<Modal.Title>{formatMessage({id: 'opportunities.add_project_dates_title'})}</Modal.Title>
			<Modal.Content data-cy="add-opportunity-dates-modal">
				<FlexColumn gap="l">
					<ContextRow label={formatMessage({id: 'common.project'})}>
						<FlexRow gap="s" alignItems="center">
							{projectCustomId ? <ColoredIdIcon color={projectColor}>{projectCustomId}</ColoredIdIcon> : null}
							<Text data-cy="add-dates-project-name">{projectName ?? ''}</Text>
						</FlexRow>
					</ContextRow>
					{/* Always rendered, even for a client-less opportunity — the design keeps the row and the layout
					    would otherwise collapse. An opportunity with no client falls back to "None". */}
					<ContextRow label={formatMessage({id: 'common.client'})}>
						<Text data-cy="add-dates-client-name">{clientName || formatMessage({id: 'common.none'})}</Text>
					</ContextRow>
					{/* The same start → end range control the Create Opportunity modal uses for its Dates field. */}
					<FlexColumn gap="xs">
						<Label>{formatMessage({id: 'common.dates'})}</Label>
						<DateRangePicker
							data-cy="add-dates-range"
							aria-label={formatMessage({id: 'common.dates'})}
							startDate={interval.startDate}
							endDate={interval.endDate}
							onChange={(next: DateInterval) => setInterval({startDate: next.startDate, endDate: next.endDate})}
						/>
					</FlexColumn>
				</FlexColumn>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton onPress={handleAdd} dataCy="add-dates-submit" disabled={unchanged || saving}>
					{formatMessage({id: 'common.add'})}
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={closeModal} dataCy="modal-cancel">
					{formatMessage({id: 'common.cancel'})}
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export default AddOpportunityDatesModal;
